import { get, omitBy } from "lodash/fp";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Checkbox,
  Container,
  Content,
  FlexboxGrid,
  Footer,
  Header,
  Pagination,
  Table,
  Input,
  InputGroup,
  Loader,
  SelectPicker,
} from "rsuite";
import SearchIcon from "@rsuite/icons/Search";
import CloseIcon from "@rsuite/icons/Close";
import { MusicScheduleServices } from "../../../core/services/MusicScheduleServices";
import { UtilityServices } from "../../../core/services/UtilityServices";
import { StoreFields, StoreListParams } from "../Stores/StoreDataTypes";
import "./MusicSchedules.css";
import {
  isEmptyVal,
  isNotEmpty,
  displayField,
  countCommaSeparatedVals,
} from "../../../utils/helpers";
import CustomCell from "../../components/Elements/CustomCell";
import CustomHeaderCell from "../../components/Elements/CustomHeaderCell";
import CheckCell from "../../components/Elements/CheckCell";
import { ToastContainer } from "react-toastify";
import {
  messageTypeArr,
  appConstObj,
  roleTypeArr,
} from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";
import { updateIn } from "json-immut";
import { StoreServices } from "../../../core/services/StoreServices";
import { useSelector } from "react-redux";
import PhoneCell from "../../components/Elements/PhoneCell";
import AddressCell from "../../components/Elements/AddressCell";

function MusicScheduleStores({
  musicScheduleId,
  companyId,
  groupId,
  withHeader = true,
  storeSearchParams = {
    smart_search: "",
  },
  setSelectedStores,
  ...props
}: {
  musicScheduleId?: number;
  companyId?: number;
  groupId?: number;
  withHeader?: boolean;
  storeSearchParams?: any;
  setSelectedStores?: Function;
}) {
  const { t } = useTranslation();
  const { Cell, Column } = Table;
  const company = useSelector((state: any) => state.company);
  const [musicScheduleInfo, setMusicScheduleInfo] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [rows, setRows] = useState(appConstObj.record_per_page);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<StoreFields[]>([]);
  const [sort_by, setSortBy] = useState<any>();
  const [sort_order, setSortOrder] = useState<any>();
  const [smartSearchInput, setSmartSearch] = useState<string>(
    storeSearchParams.smart_search
  );
  const [storeRegionSearch, setStoreRegionSearch] = useState<string>(
    storeSearchParams.region
  );
  const [storeGroupSearch, setStoreGroupSearch] = useState<string>(
    storeSearchParams.group
  );
  const loggedInUserDetail = UtilityServices.getLoggedInUserDetails();
  const initSearchFormData = {
    smart_search: storeSearchParams.smart_search,
  };

  const [formData, setFormData] = useState<StoreListParams>(initSearchFormData);
  const navigate = useNavigate();
  const [checkedKeys, setCheckedKeys] = useState<any[]>([]);
  let checked = false;
  let indeterminate = false;

  if (checkedKeys?.length === data?.length) {
    checked = true;
  } else if (checkedKeys?.length === 0) {
    checked = false;
  } else if (checkedKeys?.length > 0 && checkedKeys?.length < data?.length) {
    indeterminate = true;
  }

  const handleCheckAll = (value: any, checked: any) => {
    const keys = checked ? data.map((store) => store.id) : [];
    setCheckedKeys(keys);
    setSelectedStores && setSelectedStores(keys);
  };

  const handleCheck = (value: number, checked: any) => {
    const keys = checked
      ? [...checkedKeys, value]
      : checkedKeys.filter((store) => store !== value);
    setCheckedKeys(keys);
    setSelectedStores && setSelectedStores(keys);
  };

  function getCompanyId() {
    let compId = null;
    if (UtilityServices.getLoggedInUser("user_type") === roleTypeArr.client) {
      compId = UtilityServices.getLoggedInUser("company_id");
    } else if (company?.id) {
      compId = company.id;
    } else if (companyId > 0) {
      compId = companyId;
    }
    return compId;
  }

  function getGroupId(params?: any) {
    let grpId = null;
    if (!isEmptyVal(params?.group)) {
      grpId = params.group;
    } else if (!isEmptyVal(groupId)) {
      grpId = groupId;
    } else if (!isEmptyVal(musicScheduleInfo?.store_group_id)) {
      grpId = musicScheduleInfo.store_group_id;
    }
    return grpId;
  }

  async function getMusicScheduleStores(params: any) {
    try {
      let cmpId = getCompanyId();
      let groupId = getGroupId(params);
      if (isEmptyVal(groupId)) {
        return;
      } else {
        params.group = groupId;
      }
      if (!isEmptyVal(cmpId)) params.company = cmpId;
      if (!isEmptyVal(musicScheduleId)) params.music_schedule = musicScheduleId;
      setLoading(true);
      const res = await StoreServices.getStores(params);
      setData(get("data.data", res));
      setTotal(get("data.total", res));
      setCheckedKeys([]);
      setSelectedStores && setSelectedStores([]);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }

  async function getViewMusicScheduleDetail(id: number) {
    try {
      setLoadingSchedule(true);
      const res = await MusicScheduleServices.viewMusicSchedule(id);
      setMusicScheduleInfo({ ...res?.data?.data });
      if (res?.data?.data?.store_group_id > 0) {
        getMusicScheduleStores(
          getSearchParams({
            group: res?.data?.data?.store_group_id,
            page: page,
            sort_by: sort_by,
            sort_order: sort_order,
            rows: rows,
          })
        );
      }

      setLoadingSchedule(false);
    } catch (error) {
      setLoadingSchedule(false);
    }
  }

  const handleSearchFormChange = (data: any) => {
    let form = updateIn(formData, [data.path], data.value);
    setFormData(form);
  };

  async function init() {
    if (musicScheduleId > 0) {
      await getViewMusicScheduleDetail(musicScheduleId);
    } else if (groupId > 0) {
      getMusicScheduleStores(
        getSearchParams({
          group: groupId,
          page: page,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
    }
  }

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {}, [company.id]);

  function getSearchParams(data?: any) {
    let paramsData = {
      ...formData,
      ...{ smart_search: smartSearchInput },
      ...{ sort_by, sort_order },
      ...{ rows },
      ...data,
    };
    let params: StoreListParams = omitBy(isEmptyVal, paramsData);
    if (data?.page) {
      params.page = data?.page;
    }
    if (data?.group) {
      params.group = data?.group;
    }
    let cmpId = getCompanyId();
    if (cmpId > 0) {
      params.company = cmpId;
    }
    return params;
  }

  function onSortChange(dataKey: any, sortType: any) {
    setPage(1);
    setSortBy(dataKey);
    setSortOrder(sortType);
    getMusicScheduleStores(
      getSearchParams({
        page: 1,
        sort_by: dataKey,
        sort_order: sortType,
        rows: rows,
      })
    );
  }

  const filterMusicScheduleStores = (e: any) => {
    if (e.key === "Enter" || e.type === "click") {
      getMusicScheduleStores(
        getSearchParams({ smart_search: smartSearchInput, rows: rows })
      );
    }
  };

  const clearSmartSearch = () => {
    setSmartSearch("");
    setStoreGroupSearch("");
    setStoreRegionSearch("");
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    getMusicScheduleStores(
      getSearchParams({
        smart_search: "",
        page: 1,
        sort_by: null,
        sort_order: null,
      })
    );
  };

  return (
    <div className="music-schedule-songs">
      {loadingSchedule && (
        <Loader
          style={{ zIndex: 9999 }}
          size="lg"
          content="Loading..."
          center
          backdrop={true}
        />
      )}
      <Container className="container">
        {withHeader && (
          <Header className="h4">
            <FlexboxGrid justify="space-between">
              <FlexboxGrid.Item colspan={24}>
                <h4>
                  {t("MUSIC_SCHEDULE")} {musicScheduleInfo?.playlist_title}{" "}
                  {t("STORES")}
                </h4>
              </FlexboxGrid.Item>
            </FlexboxGrid>
          </Header>
        )}

        <Content className="music-schedule-items">
          {UtilityServices.hasLoggedInUserType(roleTypeArr.backend) && (
            <>
              <div className="row">
                <div className="col-md-8">
                  <InputGroup>
                    <Input
                      className="form-control"
                      placeholder={t("SMART_SEARCH_BY", {
                        entity:
                          t("STORE").toLowerCase() +
                          " " +
                          t("NAME").toLowerCase() +
                          t("OR").toLowerCase() +
                          " " +
                          t("CODE").toLowerCase(),
                      })}
                      value={smartSearchInput}
                      onChange={(val: any) => setSmartSearch(val)}
                      onKeyDown={(e: any) => filterMusicScheduleStores(e)}
                    />
                    <InputGroup.Button
                      title={t("SEARCH")}
                      onClick={(e: any) => filterMusicScheduleStores(e)}
                    >
                      <SearchIcon />
                    </InputGroup.Button>
                    <InputGroup.Button
                      title={t("CLEAR")}
                      onClick={() => clearSmartSearch()}
                    >
                      <CloseIcon />
                    </InputGroup.Button>
                  </InputGroup>
                </div>
              </div>
            </>
          )}

          <div className={total > 0 ? "commercial-table" : "no-record-table"}>
            <Table
              height={400}
              fillHeight={true}
              data={data}
              loading={loading}
              onSortColumn={onSortChange}
              sortColumn={sort_by}
              bordered={true}
              cellBordered
              sortType={sort_order}
              hover={true}
              renderEmpty={() => (
                <div style={{ textAlign: "center", padding: "20px" }}>
                  <h4>
                    {t("NO_ENTITY_FOUND_FOR", {
                      entity: t("STORE"),
                      for: musicScheduleInfo?.store_group?.group_name,
                    })}
                  </h4>
                </div>
              )}
            >
              <Column width={40} align="center" fixed>
                <CustomHeaderCell>
                  <div>
                    <Checkbox
                      inline
                      checked={checked}
                      indeterminate={indeterminate}
                      onChange={handleCheckAll}
                    />
                  </div>
                </CustomHeaderCell>
                <CheckCell
                  dataKey="id"
                  checkedKeys={checkedKeys}
                  onChange={handleCheck}
                />
              </Column>

              <Column width={200} sortable resizable fixed>
                <CustomHeaderCell>
                  {t("STORE")} {t("NAME")}
                </CustomHeaderCell>
                <CustomCell dataKey="store_name">
                  {(rowData: any) => (
                    <span
                      title={
                        (isNotEmpty(`${rowData.store_code}`) &&
                          `${rowData.store_code}`) ||
                        `${rowData.store_name}`
                      }
                    >
                      {displayField(rowData.store_name)}
                    </span>
                  )}
                </CustomCell>
              </Column>
              <Column flexGrow={1} resizable>
                <CustomHeaderCell>{t("ADDRESS")}</CustomHeaderCell>
                <AddressCell rowData={(rowData: any) => rowData} />
              </Column>
              <Column width={120}>
                <CustomHeaderCell>{t("PHONE")}</CustomHeaderCell>
                <PhoneCell
                  positionFromRight="30%"
                  rowData={(rowData: any) => rowData}
                />
              </Column>
              <Column width={160} resizable>
                <CustomHeaderCell>{t("REGION")}</CustomHeaderCell>
                <CustomCell dataKey="region_name">
                  {(rowData: any) => (
                    <span>{displayField(rowData.region_name)}</span>
                  )}
                </CustomCell>
              </Column>

              <Column width={160} resizable>
                <CustomHeaderCell>{t("GROUP")}</CustomHeaderCell>
                <CustomCell dataKey="group_name">
                  {(rowData: any) => (
                    <span>{displayField(rowData.group_name)}</span>
                  )}
                </CustomCell>
              </Column>
            </Table>
          </div>
        </Content>
        <Footer className="footer">
          <Pagination
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="xs"
            layout={["total"]}
            total={total}
          />
        </Footer>
        <ToastContainer />
      </Container>
    </div>
  );
}

export default MusicScheduleStores;
